import {
  PHYS_SCALE,
  FIXED_TIMESTEP,
  POSITION_ITERATIONS,
  VELOCITY_ITERATIONS,
  b2AABB,
  b2Body,
  b2BodyDef,
  b2FixtureDef,
  b2PolygonShape,
  b2Vec2,
  b2World,
} from "./box2d-compat.js";
import { PlayerBox } from "./player-box.js";
import { MyContactListener } from "./contact-listener.js";
import { displayBounds } from "./flash-display-bounds.js";

const LEFT_KEYS = ["ArrowLeft", "KeyA"];
const RIGHT_KEYS = ["ArrowRight", "KeyD"];
const UP_KEYS = ["ArrowUp", "KeyW", "Space"];
const CAMERA_EASE = 1 - Math.pow(2, -10 / 31);
const CHECKPOINT_FRAME_COUNT = 5;
const LEVEL_AIM_FRAME_COUNT = 20;
const DEATH_DELAY_FRAMES = Math.round(1200 / (1000 / 31));
const STOCK_WIN_DELAY_FRAMES = 88;
const FIXED_WIN_DELAY_FRAMES = 89;

function sortedFramePaths(folder, frameCount) {
  return Array.from({ length: frameCount }, (_, index) => `${folder}/${index + 1}.png`);
}

function rectsOverlap(a, b) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function playerFlagBounds(player) {
  const size = player.radius * 2;
  const halfExtent = size / 2 * (Math.abs(Math.cos(player.body.GetAngle())) + Math.abs(Math.sin(player.body.GetAngle())));
  return {
    x: player.x - halfExtent,
    y: player.y - halfExtent,
    width: halfExtent * 2,
    height: halfExtent * 2,
  };
}

function drawStageImage(ctx, image, levelData) {
  const { layout, pngLayout } = levelData;
  if ((levelData.imagePath ?? "").endsWith(".png") && pngLayout) {
    ctx.drawImage(
      image,
      pngLayout.cropX,
      pngLayout.cropY,
      pngLayout.cropWidth,
      pngLayout.cropHeight,
      layout.imageOffsetX,
      layout.imageOffsetY,
      layout.imageWidth,
      layout.imageHeight,
    );
    return;
  }
  ctx.drawImage(image, layout.imageOffsetX, layout.imageOffsetY);
}

function drawSymbol(ctx, image, symbolData, x, y, rotation = 0) {
  if (!image?.complete || !symbolData?.layout) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  if (symbolData.pngLayout) {
    const scaleX = symbolData.layout.imageWidth / symbolData.pngLayout.sourceWidth;
    const scaleY = symbolData.layout.imageHeight / symbolData.pngLayout.sourceHeight;
    ctx.drawImage(
      image,
      symbolData.pngLayout.cropX,
      symbolData.pngLayout.cropY,
      symbolData.pngLayout.cropWidth,
      symbolData.pngLayout.cropHeight,
      symbolData.layout.imageOffsetX + symbolData.pngLayout.cropX * scaleX,
      symbolData.layout.imageOffsetY + symbolData.pngLayout.cropY * scaleY,
      symbolData.pngLayout.cropWidth * scaleX,
      symbolData.pngLayout.cropHeight * scaleY,
    );
  } else {
    ctx.drawImage(image, symbolData.layout.imageOffsetX, symbolData.layout.imageOffsetY);
  }
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbolData, x, y, rotation = 0) {
  if (!image?.complete || !symbolData?.layout) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.drawImage(
    image,
    symbolData.layout.imageOffsetX,
    symbolData.layout.imageOffsetY,
    symbolData.layout.imageWidth,
    symbolData.layout.imageHeight,
  );
  ctx.restore();
}

export class LevelOneRuntime {
  constructor(levelData, clock, { audioEnabled = true, assetsEnabled = true, fixedLevelTransitionTiming = false } = {}) {
    this.levelData = levelData;
    this.clock = clock;
    this.world = new b2World(new b2Vec2(0, 10), true);
    this.contactListener = new MyContactListener();
    this.world.SetContactListener(this.contactListener);

    this.beginFlag = true;
    this.complete = false;
    this.failed = false;
    this.winActive = false;
    this.winCounter = -1;
    this.deathCounter = -1;
    this.camera = { x: 0, y: 0 };
    this.spikesKill = false;
    this.staticBodies = [];
    this.particles = [];
    this.assetsEnabled = assetsEnabled;
    this.fixedLevelTransitionTiming = fixedLevelTransitionTiming === true;
    this.winDelayFrames = this.fixedLevelTransitionTiming ? FIXED_WIN_DELAY_FRAMES : STOCK_WIN_DELAY_FRAMES;

    this.stageImage = this.assetsEnabled ? new Image() : null;
    if (this.stageImage) {
      this.stageImage.src = levelData.foregroundSvgPath ?? levelData.svgPath;
    }

    this.backgroundImage = this.assetsEnabled ? new Image() : null;
    if (this.backgroundImage) {
      this.backgroundImage.src = levelData.symbols.backGroundMC.imagePath;
    }
    this.backgroundStart = levelData.layout.namedChildren.backGroundMC;
    this.backgroundSymbol = levelData.symbols.backGroundMC;

    this.playerImage = this.assetsEnabled ? new Image() : null;
    if (this.playerImage) {
      this.playerImage.src = "scratch/l1_png/DefineSprite_23_PlayerBox/1.png";
    }

    this.goalFrames = this.assetsEnabled ? sortedFramePaths("scratch/l1_png/DefineSprite_68", LEVEL_AIM_FRAME_COUNT).map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    }) : [];
    this.goalCurrentFrame = 1;
    this.goalPlaying = false;

    this.checkpointFrames = this.assetsEnabled ? sortedFramePaths("scratch/l1_png/DefineSprite_85", CHECKPOINT_FRAME_COUNT).map((src) => {
      const image = new Image();
      image.src = src;
      return image;
    }) : [];
    this.checkpointCurrentFrame = 1;
    this.checkpointPlaying = false;

    this.goalPosition = this.levelData.layout.namedChildren.levelAim;
    this.checkpointRect = this.levelData.layout.namedChildren.checkPoint0;
    this.goalSymbol = this.levelData.symbols.levelAim;
    this.checkpointSymbol = this.levelData.symbols.checkPoint0;
    this.goalRect = displayBounds(this.goalPosition, this.goalSymbol, "levelAim");
    this.spawn = { x: this.checkpointRect.x, y: this.checkpointRect.y };
    this.audioEnabled = audioEnabled;
    this.levelSound = this.audioEnabled && this.assetsEnabled ? new Audio("decomp/export/sounds/12_LevelSound.mp3") : null;

    this.createBodies();
    this.resetPlayer();
  }

  setSpikesKill(enabled) {
    this.spikesKill = enabled;
  }

  setFixedLevelTransitionTiming(enabled) {
    this.fixedLevelTransitionTiming = enabled === true;
    this.winDelayFrames = this.fixedLevelTransitionTiming ? FIXED_WIN_DELAY_FRAMES : STOCK_WIN_DELAY_FRAMES;
  }

  createBodies() {
    for (const entry of this.levelData.bodies) {
      const layout = this.levelData.layout.namedChildren[entry.name];
      if (!layout) {
        continue;
      }
      const bodyDef = new b2BodyDef();
      bodyDef.type = b2Body.b2_staticBody;
      bodyDef.position.Set(layout.x / PHYS_SCALE, layout.y / PHYS_SCALE);
      bodyDef.angle = ((layout.rotation ?? 0) * Math.PI) / 180;
      bodyDef.userData = { __kind: "body", name: entry.name };
      const body = this.world.CreateBody(bodyDef);
      for (const polygon of entry.data) {
        const fixtureDef = new b2FixtureDef();
        fixtureDef.density = entry.density;
        fixtureDef.friction = entry.friction;
        fixtureDef.restitution = entry.restitution;
        fixtureDef.shape = new b2PolygonShape();
        const vertices = polygon.map(([x, y]) => new b2Vec2(x / PHYS_SCALE, y / PHYS_SCALE));
        fixtureDef.shape.SetAsArray(vertices, vertices.length);
        body.CreateFixture(fixtureDef);
      }
      this.staticBodies.push(body);
    }
  }

  resetPlayer() {
    if (this.player?.alive) {
      this.player.kill(this.world);
    }
    this.player = new PlayerBox(this.world, this.spawn.x, this.spawn.y);
    this.player.body.SetUserData({ __kind: "player", ref: this.player });
    this.camera.x = -this.player.x + 275;
    this.camera.y = -this.player.y + 200;
    this.particles = [];
    this.deathCounter = -1;
    this.failed = false;
    this.winActive = false;
    this.complete = false;
    this.beginFlag = true;
    this.goalCurrentFrame = 1;
    this.goalPlaying = false;
    this.checkpointCurrentFrame = 1;
    this.checkpointPlaying = false;
    this.clock.reset();
  }

  getBodyAtPoint(x, y, includeStatic = false) {
    const point = new b2Vec2(x, y);
    const aabb = new b2AABB();
    aabb.lowerBound.Set(x - 0.001, y - 0.001);
    aabb.upperBound.Set(x + 0.001, y + 0.001);
    const fixtures = [];
    const broadPhase = this.world?.m_broadPhase;
    const count = typeof broadPhase?.QueryAABB === "function"
      ? broadPhase.QueryAABB(aabb, fixtures, 10)
      : 0;
    for (let index = 0; index < count; index += 1) {
      const fixture = fixtures[index];
      const body = fixture?.GetBody?.() ?? fixture?.m_body ?? null;
      const shape = fixture?.GetShape?.() ?? fixture?.m_shape ?? fixture;
      if (!body || !shape) {
        continue;
      }
      if ((body.GetType() !== b2Body.b2_staticBody || includeStatic) && shape.TestPoint(body.GetTransform(), point)) {
        return body;
      }
    }
    return null;
  }

  getPlayerRect() {
    return playerFlagBounds(this.player);
  }

  playerOverlapsRect(rect) {
    return Boolean(rect && this.player?.isLive?.() && rectsOverlap(this.getPlayerRect(), rect));
  }

  startDeath() {
    if (!this.player?.isLive() || this.deathCounter >= 0) {
      return;
    }
    const velocity = this.player.body.GetLinearVelocity();
    this.deathCounter = DEATH_DELAY_FRAMES;
    for (let index = 0; index < 8; index += 1) {
      this.particles.push({
        x: this.player.x - 4 + 5 * Math.random() - 5 * Math.random(),
        y: this.player.y - 4 + 5 * Math.random() - 5 * Math.random(),
        vx: velocity.x / 3 * PHYS_SCALE + 7 * Math.cos(index * 2 * Math.PI / 8),
        vy: velocity.y / 3 * PHYS_SCALE + 7 * Math.sin(index * 2 * Math.PI / 8),
      });
    }
    this.player.kill(this.world);
  }

  startWin() {
    if (this.goalPlaying || this.complete) {
      return;
    }
    this.goalPlaying = true;
    this.winActive = true;
    this.winCounter = this.winDelayFrames;
    this.clock.Freeze(this.winDelayFrames);
    this.player.body.SetLinearDamping(3);
    this.player.body.SetAngularDamping(3);
    if (this.levelSound) {
      try {
        this.levelSound.currentTime = 0;
        void this.levelSound.play();
      } catch {
        // Browser autoplay restrictions can block this.
      }
    }
  }

  updateAnimations() {
    if (this.checkpointPlaying && this.checkpointCurrentFrame < CHECKPOINT_FRAME_COUNT) {
      this.checkpointCurrentFrame += 1;
    } else if (this.checkpointPlaying) {
      this.checkpointPlaying = false;
    }

    if (this.goalPlaying && this.goalCurrentFrame < LEVEL_AIM_FRAME_COUNT) {
      this.goalCurrentFrame += 1;
    }

    if (this.particles.length > 0) {
      for (const particle of this.particles) {
        particle.vy += 0.35;
        particle.x += particle.vx * FIXED_TIMESTEP;
        particle.y += particle.vy * FIXED_TIMESTEP;
      }
    }
  }

  update(input) {
    this.updateAnimations();

    if (this.deathCounter >= 0) {
      this.deathCounter -= 1;
      if (this.deathCounter <= 0) {
        this.failed = true;
      }
      return;
    }

    if (this.winCounter >= 0) {
      this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
      this.world.ClearForces();
      this.clock.update();
      const targetCameraX = -this.player.x + 275;
      const targetCameraY = -this.player.y + 200;
      this.camera.x += (targetCameraX - this.camera.x) * CAMERA_EASE;
      this.camera.y += (targetCameraY - this.camera.y) * CAMERA_EASE;
      this.winCounter -= 1;
      if (this.winCounter <= 0) {
        this.winActive = false;
        this.complete = true;
      }
      return;
    }

    this.world.Step(FIXED_TIMESTEP, VELOCITY_ITERATIONS, POSITION_ITERATIONS);
    this.world.ClearForces();

    const position = this.player.body.GetPosition();
    const centerSupport = Boolean(this.getBodyAtPoint(position.x, position.y + 0.4, true));
    const leftSupport = Boolean(this.getBodyAtPoint(position.x - 0.2, position.y + 0.38, true));
    const rightSupport = Boolean(this.getBodyAtPoint(position.x + 0.2, position.y + 0.38, true));
    const hasGroundSupport = centerSupport || leftSupport || rightSupport;
    const velocity = this.player.body.GetLinearVelocity();

    if (LEFT_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (hasGroundSupport) {
        if (velocity.x > -5) {
          this.player.body.SetLinearVelocity(new b2Vec2(velocity.x - 0.5, velocity.y));
        }
      } else if (velocity.x > -2.5) {
        this.player.body.SetLinearVelocity(new b2Vec2(velocity.x - 0.25, velocity.y));
      }
    }

    const movingRight = RIGHT_KEYS.some((code) => input.isDown(code));
    if (this.beginFlag && movingRight) {
      this.clock?.unpause?.();
      this.beginFlag = false;
    }
    if (movingRight) {
      this.player.body.SetAwake(true);
      if (hasGroundSupport) {
        if (velocity.x < 5) {
          this.player.body.SetLinearVelocity(new b2Vec2(velocity.x + 0.5, velocity.y));
        }
      } else if (velocity.x < 2.5) {
        this.player.body.SetLinearVelocity(new b2Vec2(velocity.x + 0.25, velocity.y));
      }
    }

    if (UP_KEYS.some((code) => input.isDown(code))) {
      this.player.body.SetAwake(true);
      if (this.contactListener.playerContactBodies.length > 0 && hasGroundSupport) {
        this.player.body.ApplyForce(new b2Vec2(0, -65), this.player.body.GetWorldCenter());
      } else if (velocity.y < 0) {
        this.player.body.ApplyForce(new b2Vec2(0, -1), this.player.body.GetWorldCenter());
      }
    }

    if (this.playerOverlapsRect(this.checkpointRect) && this.checkpointCurrentFrame === 1) {
      this.checkpointPlaying = true;
    }
    if (this.playerOverlapsRect(this.goalRect) && this.goalCurrentFrame === 1) {
      this.startWin();
    }
    if (this.player.y > 550) {
      this.startDeath();
    }

    this.clock.update();

    const targetCameraX = -this.player.x + 275;
    const targetCameraY = -this.player.y + 200;
    this.camera.x += (targetCameraX - this.camera.x) * CAMERA_EASE;
    this.camera.y += (targetCameraY - this.camera.y) * CAMERA_EASE;
  }

  renderBackground(ctx) {
    if (!this.backgroundImage.complete) {
      return;
    }
    drawSymbol(
      ctx,
      this.backgroundImage,
      this.backgroundSymbol,
      this.backgroundStart.x - this.camera.x / 10,
      this.backgroundStart.y - this.camera.y / 10,
      0,
    );
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);
    this.renderBackground(ctx);
    ctx.translate(this.camera.x, this.camera.y);

    if (this.stageImage.complete) {
      drawStageImage(ctx, this.stageImage, {
        ...this.levelData,
        imagePath: this.levelData.foregroundSvgPath ?? this.levelData.svgPath,
      });
    }

    const checkpointFrame = this.checkpointFrames[this.checkpointCurrentFrame - 1];
    if (checkpointFrame?.complete) {
      drawAnimatedFrame(ctx, checkpointFrame, this.checkpointSymbol, this.checkpointRect.x, this.checkpointRect.y);
    }

    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    if (goalFrame?.complete) {
      drawAnimatedFrame(ctx, goalFrame, this.goalSymbol, this.goalPosition.x, this.goalPosition.y);
    }

    if (this.player?.isLive()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
